import { TicketLayout } from "@jambushrushti/components/tickets/TicketLayout";
import { Seo } from "@jambushrushti/components/shared/Seo";
import {
  useTickets,
  useTicketCategories,
} from "@jambushrushti/hooks/useTickets";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@jambushrushti/components/ui/card";
import { Button } from "@jambushrushti/components/ui/button";
import { Badge } from "@jambushrushti/components/ui/badge";
import {
  BarChart3,
  Download,
  FileText,
  PieChart,
  TrendingUp,
} from "lucide-react";

export default function TicketReports() {
  const { data: tickets = [] } = useTickets();
  const { data: categories = [] } = useTicketCategories();

  // Calculate stats
  const totalTickets = tickets.length;
  const resolvedTickets = tickets.filter(t =>
    ["resolved", "closed"].includes(t.status)
  ).length;
  const avgResolutionTime = "Unavailable";

  const slaBreached = tickets.filter(t => t.sla_breached).length;
  const slaCompliance =
    totalTickets > 0
      ? Math.round(((totalTickets - slaBreached) / totalTickets) * 100)
      : 100;

  const categoryStats = categories
    .map(cat => ({
      name: cat.name,
      count: tickets.filter(t => t.category_id === cat.id).length,
    }))
    .sort((a, b) => b.count - a.count);

  const priorityStats = {
    critical: tickets.filter(t => t.priority === "critical").length,
    high: tickets.filter(t => t.priority === "high").length,
    medium: tickets.filter(t => t.priority === "medium").length,
    low: tickets.filter(t => t.priority === "low").length,
  };

  const handleExport = (type: string) => {
    alert(`Exporting ${type} report... (Mock functionality)`);
  };

  return (
    <TicketLayout title="Reports">
      <Seo
        title="Ticket Reports | Jambushrusti"
        description="View analytics and reports for ticket management."
      />

      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div>
            <h1 className="font-heading text-2xl font-bold text-foreground">
              Reports & Analytics
            </h1>
            <p className="text-muted-foreground">
              View performance metrics and generate reports.
            </p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => handleExport("excel")}>
              <Download className="mr-2 h-4 w-4" />
              Export Excel
            </Button>
            <Button variant="outline" onClick={() => handleExport("pdf")}>
              <FileText className="mr-2 h-4 w-4" />
              Export PDF
            </Button>
          </div>
        </div>

        {/* Key Metrics */}
        <div className="grid gap-4 md:grid-cols-4">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10">
                  <BarChart3 className="h-6 w-6 text-primary" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{totalTickets}</p>
                  <p className="text-sm text-muted-foreground">Total Tickets</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-green-100">
                  <TrendingUp className="h-6 w-6 text-green-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{resolvedTickets}</p>
                  <p className="text-sm text-muted-foreground">Resolved</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-blue-100">
                  <PieChart className="h-6 w-6 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{slaCompliance}%</p>
                  <p className="text-sm text-muted-foreground">
                    SLA Compliance
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="bg-orange/10 flex h-12 w-12 items-center justify-center rounded-xl">
                  <BarChart3 className="text-orange h-6 w-6" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{avgResolutionTime}</p>
                  <p className="text-sm text-muted-foreground">
                    Avg Resolution
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="grid gap-6 lg:grid-cols-2">
          {/* By Category */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Tickets by Category</CardTitle>
            </CardHeader>
            <CardContent>
              {categoryStats.length > 0 ? (
                <div className="space-y-3">
                  {categoryStats.map(cat => (
                    <div
                      key={cat.name}
                      className="flex items-center justify-between"
                    >
                      <span className="text-sm">{cat.name}</span>
                      <div className="flex items-center gap-3">
                        <div className="h-2 w-32 overflow-hidden rounded-full bg-muted">
                          <div
                            className="h-full bg-primary"
                            style={{
                              width: `${totalTickets > 0 ? (cat.count / totalTickets) * 100 : 0}%`,
                            }}
                          />
                        </div>
                        <span className="w-8 text-sm font-medium">
                          {cat.count}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="py-4 text-center text-sm text-muted-foreground">
                  No data available.
                </p>
              )}
            </CardContent>
          </Card>

          {/* By Priority */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Tickets by Priority</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Badge
                      variant="outline"
                      className="bg-red-100 text-red-700"
                    >
                      Critical
                    </Badge>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="h-2 w-32 overflow-hidden rounded-full bg-muted">
                      <div
                        className="h-full bg-red-500"
                        style={{
                          width: `${totalTickets > 0 ? (priorityStats.critical / totalTickets) * 100 : 0}%`,
                        }}
                      />
                    </div>
                    <span className="w-8 text-sm font-medium">
                      {priorityStats.critical}
                    </span>
                  </div>
                </div>

                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Badge
                      variant="outline"
                      className="bg-orange-100 text-orange-700"
                    >
                      High
                    </Badge>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="h-2 w-32 overflow-hidden rounded-full bg-muted">
                      <div
                        className="h-full bg-orange-500"
                        style={{
                          width: `${totalTickets > 0 ? (priorityStats.high / totalTickets) * 100 : 0}%`,
                        }}
                      />
                    </div>
                    <span className="w-8 text-sm font-medium">
                      {priorityStats.high}
                    </span>
                  </div>
                </div>

                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Badge
                      variant="outline"
                      className="bg-blue-100 text-blue-700"
                    >
                      Medium
                    </Badge>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="h-2 w-32 overflow-hidden rounded-full bg-muted">
                      <div
                        className="h-full bg-blue-500"
                        style={{
                          width: `${totalTickets > 0 ? (priorityStats.medium / totalTickets) * 100 : 0}%`,
                        }}
                      />
                    </div>
                    <span className="w-8 text-sm font-medium">
                      {priorityStats.medium}
                    </span>
                  </div>
                </div>

                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Badge
                      variant="outline"
                      className="bg-gray-100 text-gray-700"
                    >
                      Low
                    </Badge>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="h-2 w-32 overflow-hidden rounded-full bg-muted">
                      <div
                        className="h-full bg-gray-400"
                        style={{
                          width: `${totalTickets > 0 ? (priorityStats.low / totalTickets) * 100 : 0}%`,
                        }}
                      />
                    </div>
                    <span className="w-8 text-sm font-medium">
                      {priorityStats.low}
                    </span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* SLA Performance */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">SLA Performance Summary</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid gap-6 md:grid-cols-3">
              <div className="rounded-xl bg-green-50 p-4 text-center">
                <p className="text-3xl font-bold text-green-600">
                  {totalTickets - slaBreached}
                </p>
                <p className="mt-1 text-sm text-muted-foreground">Within SLA</p>
              </div>
              <div className="rounded-xl bg-red-50 p-4 text-center">
                <p className="text-3xl font-bold text-red-600">{slaBreached}</p>
                <p className="mt-1 text-sm text-muted-foreground">
                  SLA Breached
                </p>
              </div>
              <div className="rounded-xl bg-primary/5 p-4 text-center">
                <p className="text-3xl font-bold text-primary">
                  {slaCompliance}%
                </p>
                <p className="mt-1 text-sm text-muted-foreground">
                  Compliance Rate
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </TicketLayout>
  );
}
